import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Stack,
    Typography,
    Divider,
    IconButton,
    Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { isNumberEmpty, printDebugLog } from "../../../constants/Constants";
import { fmtMoney } from "../../../constants/commonUtils";

// ---- ExtraOrder helpers (JS, not TS) ----
const cloneOuter = (m) => new Map(m);
const cloneInner = (m) => new Map(m);
const ensureInner = (outer, extraType) => {
    if (!outer.has(extraType)) {
        outer.set(extraType, new Map());
    } else {
        outer.set(extraType, cloneInner(outer.get(extraType)));
    }
    return outer.get(extraType);
};
const makeExtraOrder = (partial) => {
    const {
        header = "",
        itemName,
        quantity = 0,
        price = 0,
        totalPrice,
    } = partial || {};
    const q = Number(quantity) || 0;
    const p = Number(price) || 0;
    return {
        header,
        itemName,
        quantity: q,
        price: p,
        totalPrice: totalPrice != null ? Number(totalPrice) : +(q * p).toFixed(2),
    };
};


export default function ExtrasOrderUI({ groupKeys, itemDetails, extrasOrderMap, setExtraOrderMap, handleSetExtraOrderOption, updateExtraPrice, deleteExtraPrice, checkPaidStatus }) {
    // Map<string, Map<string, ExtraOrder>>

    const [rows, setRows] = useState([]);

    const buildRows = (eMap) => {
        const arr = [];
        eMap.forEach((inner, gk) => {
            inner.forEach((e) => {
                const obj = {
                    extraType: gk,
                    header: e.header,
                    itemName: e.itemName,
                    quantity: e.quantity,
                    price: e.price,
                    totalPrice: e.totalPrice != null ? e.totalPrice : e.quantity * e.price,
                }
                arr.push(obj);
            });
        });
        // sort optional
        arr.sort((a, b) =>
            a.extraType === b.extraType
                ? a.itemName.localeCompare(b.itemName)
                : a.extraType.localeCompare(b.extraType)
        );
        return arr;
    }

    useEffect(() => {
        printDebugLog("ExtrasOrderUI", extrasOrderMap);
        setRows(buildRows(extrasOrderMap))
    }, [extrasOrderMap]);


    // Simple form state
    const [form, setForm] = useState({
        extraType: "",
        itemName: "",
        header: "",
        quantity: "",
        price: "",
    });
    // Lock itemName after an item exists / when editing
    const [lockItemName, setLockItemName] = useState(false);
    const [oldPrice, setOldPrice] = useState(0);

    const handleField = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    // Check if this item exists (to toggle Delete button etc.)
    const itemExists = useMemo(() => {
        if (!form.extraType || !form.itemName) return false;
        const inner = extrasOrderMap.get(form.extraType);
        return !!inner?.has(form.itemName);
    }, [extrasOrderMap, form.extraType, form.itemName]);

    // CRUD ops (immutable)
    const upsertExtra = (extraType, extra) => {
        setExtraOrderMap((prev) => {
            const next = cloneOuter(prev || new Map());
            const e = makeExtraOrder(extra);
            if (!e.itemName) return prev; // nothing to do
            const inner = ensureInner(next, extraType);
            inner.set(e.itemName, e);
            return next;
        });
        const tPrice = Number(extra?.quantity ?? 0) * Number(extra?.price ?? 0);
        updateExtraPrice(oldPrice, tPrice);
    };

    const removeExtra = (extraType, itemName) => {
        setExtraOrderMap((prev) => {
            if (!prev?.has(extraType)) return prev;

            const next = cloneOuter(prev);
            const inner = cloneInner(next.get(extraType));

            //  Get the extra object BEFORE deleting
            const extra = inner.get(itemName);

            if (extra) {
                // Calculate price based on extra details
                const tPrice = Number(extra?.quantity ?? 0) * Number(extra?.price ?? 0);
                deleteExtraPrice(tPrice);
            }

            // Now safely delete the item from the inner map
            inner.delete(itemName);

            if (inner.size === 0) {
                next.delete(extraType);
            } else {
                next.set(extraType, inner);
            }

            return next;
        });
    };


    const clearForm = () => {
        setForm({ extraType: null, itemName: "", header: "", quantity: "", price: "" });
        setLockItemName(false);
    };

    const handleAddOrUpdate = () => {
        if (!form.extraType || !form.itemName || !form.quantity || !form.price) return;
        upsertExtra(form.extraType, {
            header: form.header,
            itemName: form.itemName,
            quantity: form.quantity,
            price: form.price,
        });
        clearForm();
    };

    const handleDeleteCurrent = () => {
        if (!itemExists) return;
        removeExtra(form.extraType, form.itemName);
        clearForm();
    };

    const handleEditRow = (row) => {
        setForm({
            extraType: row.extraType,
            itemName: row.itemName,
            header: row.header || "",
            quantity: String(row.quantity ?? ""),
            price: String(row.price ?? ""),
        });
        const tPrice = Number(row?.quantity ?? 0) * Number(row?.price ?? 0);
        setOldPrice(tPrice);
        setLockItemName(true);
    };

    const handleDeleteRow = (row) => {
        if (checkPaidStatus()) {
            return;
        }
        removeExtra(row.extraType, row.itemName);
        // if we were editing this one, clear the form
        setOldPrice(0);
        if (
            lockItemName &&
            form.extraType === row.extraType &&
            form.itemName === row.itemName
        ) {
            clearForm();
        }
    };

    // Flatten map for the table


    // Totals
    const groupTotal = (extraType) => {
        const inner = extrasOrderMap.get(extraType);
        if (!inner) return 0;
        let s = 0;
        inner.forEach((e) => {
            const q = Number(e.quantity) || 0;
            const p = Number(e.price) || 0;
            s += e.totalPrice != null ? Number(e.totalPrice) : q * p;
        });
        return +s.toFixed(2);
    };
    const grandTotal = useMemo(() => {
        let s = 0;
        extrasOrderMap.forEach((inner) => {
            inner.forEach((e) => {
                const q = Number(e.quantity) || 0;
                const p = Number(e.price) || 0;
                s += e.totalPrice != null ? Number(e.totalPrice) : q * p;
            });
        });
        return +s.toFixed(2);
    }, [extrasOrderMap]);

    const currentGroupTotal =
        form.extraType ? fmtMoney(groupTotal(form.extraType)) : "0.00";

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Extra Orders
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }} elevation={4}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            id="extras-select"
                            options={groupKeys}
                            autoHighlight
                            variant="filled"
                            size="small"
                            fullWidth
                            onChange={(e, option) => {
                                if (option) {
                                    handleField("extraType")({ target: { value: option } });
                                } else {
                                    handleField("extraType")({ target: { value: "" } });
                                }
                                handleSetExtraOrderOption(option);
                            }}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={(option) => option ?? ""}
                            value={form?.extraType ?? null}
                            renderOption={(props, option) => {
                                return (
                                    <Box
                                        component="li"
                                        {...props}
                                    >
                                        {option}
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Extra Type"
                                    inputProps={{
                                        ...params.inputProps,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            id="item-select"
                            options={itemDetails}
                            autoHighlight
                            variant="filled"
                            size="small"
                            fullWidth
                            onChange={(e, option) => {
                                if (option) {
                                    if (option.id === 'Other') {
                                        setForm((f) => ({ ...f, itemName: "", price: "" }));
                                        setLockItemName(false);
                                    } else {
                                        setForm((f) => ({ ...f, itemName: option.id, price: option.predefinedPrice }));
                                        setLockItemName(true);
                                    }
                                } else {
                                    setForm((f) => ({ ...f, itemName: "", price: "" }));
                                    setLockItemName(false);
                                }
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={() => ""}
                            renderOption={(props, option) => {
                                return (
                                    <Box
                                        key={option.id}
                                        component="li"
                                        {...props}
                                    >
                                        {`${option.id}${isNumberEmpty(option.predefinedPrice) ? "" : `(₹${fmtMoney(option.predefinedPrice)})`}`}
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Item Details"
                                    inputProps={{
                                        ...params.inputProps,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="filled"
                            size="small"
                            label="Item Name"
                            value={form.itemName}
                            onChange={handleField("itemName")}
                            disabled={lockItemName}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="filled"
                            size="small"
                            label="Other Details(optional)"
                            value={form.header}
                            onChange={handleField("header")}
                            placeholder="e.g. Beverages"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="filled"
                            size="small"
                            type="number"
                            label="Quantity"
                            value={form.quantity}
                            onChange={handleField("quantity")}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="filled"
                            size="small"
                            type="number"
                            label="Price"
                            value={form.price}
                            onChange={handleField("price")}
                            inputProps={{ min: 0, step: "0.01" }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} sx={{ height: "100%" }} alignItems="center" justifyContent="flex-start">
                            <Button variant="contained" onClick={handleAddOrUpdate}>
                                {itemExists ? "Update" : "Add"}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDeleteCurrent}
                                disabled={!itemExists}
                            >
                                Delete
                            </Button>
                            <Button variant="text" onClick={clearForm}>Reset</Button>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    Current Group Total ({form.extraType || "—"}): <b>{currentGroupTotal}</b> &nbsp;|&nbsp; Grand Total: <b>{fmtMoney(grandTotal)}</b>
                </Typography>
            </Paper>

            {/* Table of all items */}
            <TableContainer component={Paper} elevation={3} sx={{
                maxHeight: 300,
                overflowY: "auto",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                border: "none",
                borderRadius: 2,
            }}
            >
                <Table aria-label="extra-orders-table">
                    <TableHead>
                        <TableRow sx={{
                            backgroundColor: "#01333e",
                            "& .MuiTableCell-root": {
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                padding: "6px 8px",
                                borderBottom: "none",
                            },
                        }}>
                            <TableCell align="center">ItemName</TableCell>
                            <TableCell align="center">Other Details</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="center">Price</TableCell>
                            <TableCell align="center">Total</TableCell>
                            <TableCell align="center">Group</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell align="center" colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        No items yet. Add some above.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((r) => {
                                // ✅ Log each row object
                                console.log("Row Data →", r);

                                return (
                                    <TableRow key={`${r.extraType}::${r.itemName}`}>
                                        <TableCell align="center">{r.itemName}</TableCell>
                                        <TableCell align="center">{r.header}</TableCell>
                                        <TableCell align="center">{r.quantity}</TableCell>
                                        <TableCell align="center">{fmtMoney(r.price)}</TableCell>
                                        <TableCell align="center">{fmtMoney(r.totalPrice)}</TableCell>
                                        <TableCell align="center">{r.extraType}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton
                                                    size="small"
                                                    aria-label="edit"
                                                    onClick={() => handleEditRow(r)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label="delete"
                                                    onClick={() => handleDeleteRow(r)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

        </Box>
    );
}
